import { EditorTheme } from '../templateEditor.theme'
import { ListOrientation, PlaceToolId, Rect } from '../templateEditor.types'
import { clampRectToCanvas } from '../templateEditor.utils'

export type ListMetricsInput = {
    fontSize?: number
    lineHeight?: number
    gap?: number
    orientation?: ListOrientation
}

const DEFAULT_FONT_SIZE = 28
const DEFAULT_LINE_HEIGHT = 1.5
const DEFAULT_GAP = 8
const DEFAULT_LIST_WIDTH = 400

const clampCount = (count: number, max: number) => Math.max(1, Math.min(max, count))

const listFontSize = (input: ListMetricsInput) => input.fontSize ?? DEFAULT_FONT_SIZE
const listLineHeight = (input: ListMetricsInput) => input.lineHeight ?? DEFAULT_LINE_HEIGHT
const listGap = (input: ListMetricsInput) => input.gap ?? DEFAULT_GAP
const listLinePx = (input: ListMetricsInput) => Math.ceil(listFontSize(input) * listLineHeight(input))

const listMetrics = (orientation: ListOrientation, input: ListMetricsInput = {}): ListMetricsInput => ({
    fontSize: listFontSize(input),
    lineHeight: listLineHeight(input),
    gap: listGap(input),
    orientation,
})

const orientationFromPlaceType = (type: string): ListOrientation | null => {
    if (type === 'list') return 'vertical'
    if (type === 'listH') return 'horizontal'
    return null
}

/** Высота одного пункта списка по типографике. */
export const listItemHeight = (input: ListMetricsInput, _theme: Pick<EditorTheme, 'listPad'>) =>
    listLinePx(input) + listGap(input)

/** Ширина слота горизонтального списка. */
export const listItemSlotWidth = (theme: Pick<EditorTheme, 'listHorizontalItemW'>) => theme.listHorizontalItemW

/** Размер списка под N пунктов. */
export const listSizeFor = (count: number, input: ListMetricsInput, theme: EditorTheme) => {
    const n = Math.max(1, count)
    const gap = listGap(input)

    if (input.orientation === 'horizontal') {
        const slot = listItemSlotWidth(theme)
        return {
            w: theme.listPad + n * slot + Math.max(0, n - 1) * gap,
            h: theme.listPad + listLinePx(input),
        }
    }

    return { w: DEFAULT_LIST_WIDTH, h: theme.listPad + n * listItemHeight(input, theme) }
}

/** Сколько пунктов помещается в нарисованный прямоугольник. */
export const listItemCountForBox = (w: number, h: number, input: ListMetricsInput, theme: EditorTheme) => {
    const gap = listGap(input)

    if (input.orientation === 'horizontal') {
        const slot = listItemSlotWidth(theme)
        return clampCount(Math.floor((Math.max(0, w - theme.listPad) + gap) / (slot + gap)), theme.maxListItems)
    }

    return clampCount(Math.floor(Math.max(0, h - theme.listPad) / listItemHeight(input, theme)), theme.maxListItems)
}

/** Строки и столбцы таблицы по размеру рамки. */
export const tableGridForBox = (w: number, h: number, theme: EditorTheme) => {
    const cols = clampCount(Math.floor(Math.max(0, w) / theme.tableColWidth), theme.maxTableCols)
    const rows = clampCount(Math.floor(Math.max(0, h - theme.listPad) / theme.rowHeight), theme.maxTableRows)

    return {
        rows,
        cols,
        rowHeight: theme.rowHeight,
        colWidth: theme.tableColWidth,
        w: cols * theme.tableColWidth,
        h: theme.listPad + rows * theme.rowHeight,
    }
}

export const tableSizeFor = (rows: number, cols: number, theme: EditorTheme) => ({
    w: cols * theme.tableColWidth,
    h: theme.listPad + rows * theme.rowHeight,
})

/** Рамка для размещения по клику — центр в точке, размер w×h. */
export const placeBoxAtPoint = (
    centerX: number,
    centerY: number,
    w: number,
    h: number,
    canvasW: number,
    canvasH: number,
    minElSize: number
): Rect => clampRectToCanvas({ x: centerX - w / 2, y: centerY - h / 2, w, h }, canvasW, canvasH, minElSize)

/** Рамка из двух точек drag (логические координаты). */
export const placeBoxFromDrag = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    canvasW: number,
    canvasH: number,
    minElSize: number
): Rect =>
    clampRectToCanvas(
        {
            x: Math.min(x1, x2),
            y: Math.min(y1, y2),
            w: Math.abs(x2 - x1),
            h: Math.abs(y2 - y1),
        },
        canvasW,
        canvasH,
        minElSize
    )

export const frameSizeForImage = (naturalW: number, naturalH: number, maxW = 640, maxH = 360) => {
    if (naturalW <= 0 || naturalH <= 0) {
        return { w: 400, h: 225 }
    }

    const scale = Math.min(maxW / naturalW, maxH / naturalH, 1)
    return { w: Math.round(naturalW * scale), h: Math.round(naturalH * scale) }
}

export type PlacePreviewMeta =
    | {
          kind: 'list'
          orientation: ListOrientation
          count: number
          itemStep: number
          pad: number
          gap: number
          label: string
          key: string
      }
    | {
          kind: 'table'
          rows: number
          cols: number
          rowHeight: number
          colWidth: number
          pad: number
          label: string
          key: string
      }

const listCountLabel = (count: number) => {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return `${count} пункт`
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} пункта`
    return `${count} пунктов`
}

const fitListToBox = (orientation: ListOrientation, rect: Rect, theme: EditorTheme, input: ListMetricsInput = {}) => {
    const base = listMetrics(orientation, input)
    const count = listItemCountForBox(rect.w, rect.h, base, theme)
    const fit = listSizeFor(count, base, theme)
    return { base, count, fit }
}

/** Метаданные и шаг полосок для превью размещения list/table. */
export const getPlacePreviewMeta = (
    type: Extract<PlaceToolId, 'list' | 'listH' | 'table'>,
    rect: Rect,
    theme: EditorTheme
): PlacePreviewMeta | null => {
    const orientation = orientationFromPlaceType(type)
    if (orientation) {
        const { base, count } = fitListToBox(orientation, rect, theme)
        const gap = listGap(base)
        const itemStep = orientation === 'horizontal' ? listItemSlotWidth(theme) + gap : listItemHeight(base, theme)

        return {
            kind: 'list',
            orientation,
            count,
            itemStep,
            pad: theme.listPad,
            gap,
            label: listCountLabel(count),
            key: `${orientation}-${count}`,
        }
    }

    if (type === 'table') {
        const grid = tableGridForBox(rect.w, rect.h, theme)
        return {
            kind: 'table',
            rows: grid.rows,
            cols: grid.cols,
            rowHeight: grid.rowHeight,
            colWidth: grid.colWidth,
            pad: theme.listPad,
            label: `${grid.cols} × ${grid.rows}`,
            key: `${grid.cols}x${grid.rows}`,
        }
    }

    return null
}

/** Подгоняет рамку превью под целое число пунктов/ячеек. */
export const snapPlacePreviewRect = (type: string, rect: Rect, theme: EditorTheme): Rect => {
    if (type === 'table') {
        const grid = tableGridForBox(rect.w, rect.h, theme)
        return { ...rect, w: grid.w, h: grid.h }
    }

    const orientation = orientationFromPlaceType(type)
    if (!orientation) {
        return rect
    }

    const { fit } = fitListToBox(orientation, rect, theme)
    return orientation === 'horizontal'
        ? { ...rect, w: fit.w, h: Math.max(rect.h, fit.h) }
        : { ...rect, w: Math.max(rect.w, theme.minElSize), h: fit.h }
}

type ListResizeElement = {
    items: string[]
    orientation: ListOrientation
    fontSize: number
    gap: number
    x: number
    y: number
    w: number
    h: number
}

type ResizeItems = (items: string[], count: number, horizontal: boolean) => string[]

export const applyListResize = (
    element: ListResizeElement,
    nextRect: Rect,
    _handle: string,
    theme: EditorTheme,
    _resizeItems: ResizeItems
): Rect & { items: string[] } => {
    const base = listMetrics(element.orientation, { fontSize: element.fontSize, gap: element.gap })
    const fit = listSizeFor(element.items.length, base, theme)

    if (element.orientation === 'horizontal') {
        return {
            x: nextRect.x,
            y: nextRect.y,
            w: Math.max(nextRect.w, fit.w, theme.minElSize),
            h: Math.max(nextRect.h, fit.h, theme.minElSize),
            items: element.items,
        }
    }

    return {
        x: nextRect.x,
        y: nextRect.y,
        w: Math.max(nextRect.w, theme.minElSize),
        h: Math.max(nextRect.h, fit.h, theme.minElSize),
        items: element.items,
    }
}
